import { useTranslation } from 'next-i18next';

import { AssociationBadge } from '@restworld/ui-components';
import { partnerLandingUrl } from '@restworld/utils-common';
import { WorkerProfessionalAssociation } from '@restworld/utility-types';

/**
 * The professional associations a candidate declared, rendered as badges.
 *
 * One component for all three employer surfaces that show them — the candidate
 * modal header, the kanban card and the shortlist card — so the wording, the
 * link and the "no logo yet" behaviour cannot drift between the place an
 * employer scans and the place they open.
 *
 * ⛔ Decorative, and it must stay that way. It carries no ordering, filtering or
 * recommendation weight anywhere in the product. Membership is bought with an
 * annual fee, so weighting it would sell a fast lane to whichever candidates can
 * afford one.
 *
 * `membershipLabel` states the relationship rather than the entity: employer
 * side the useful sentence is "this candidate belongs to X", not "X". It is
 * interpolated from the association's own label, so nothing here names APCI and
 * a second association needs no change to this file.
 *
 * Renders nothing when the candidate declared none, which is the common case.
 */
export const CandidateAssociationBadges = ({ associations }: { associations?: WorkerProfessionalAssociation[] }) => {
  const { t } = useTranslation('common');

  const variables = (associations ?? [])
    .map((association) => association?.professional_association_var)
    .filter((variable) => variable?.id);

  if (!variables.length) return null;

  return (
    <>
      {variables.map((variable) => (
        <AssociationBadge
          key={variable.id}
          label={variable.label}
          description={variable.description}
          iconUrl={variable.icon}
          membershipLabel={t('association_member', { name: variable.label })}
          linkUrl={partnerLandingUrl(variable.key)}
          linkLabel={t('find_out_more')}
        />
      ))}
    </>
  );
};

export default CandidateAssociationBadges;
